import { X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useCreateSupportTicketMutation } from '@/store/api/apiSlice';
import { TicketCategory, UserRole } from '@/types';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import SupportManagement from '@/pages/Support';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SupportModal({ isOpen, onClose }: SupportModalProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<TicketCategory>(TicketCategory.GENERAL);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [createTicket, { isLoading }] = useCreateSupportTicketMutation();

  if (!isOpen) return null;

  // If admin, show management page instead of form
  if (isAdmin) {
    return (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={onClose}
        />

        {/* Side Modal */}
        <div className="fixed right-0 top-0 h-full w-full lg:max-w-7xl bg-white z-50 shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-right duration-300">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
            <h2 className="text-2xl font-bold text-gray-900">Support Management</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            <SupportManagement />
          </div>
        </div>
      </>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!subject.trim() || !message.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await createTicket({
        subject: subject.trim(),
        message: message.trim(),
        category,
      }).unwrap();

      setSuccess(true);
      setTimeout(() => {
        setSubject('');
        setMessage('');
        setCategory(TicketCategory.GENERAL);
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to submit support ticket. Please try again.');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Side Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:max-w-2xl bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Support</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Subject */}
            <div>
              <label className="block text-base font-semibold text-gray-900 mb-2 text-start">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of your issue"
                maxLength={200}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                {subject.length}/200 characters
              </p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-base font-semibold text-gray-900 mb-2 text-start">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as TicketCategory)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent capitalize"
              >
                <option value={TicketCategory.TECHNICAL}>Technical</option>
                <option value={TicketCategory.BILLING}>Billing</option>
                <option value={TicketCategory.ACCOUNT}>Account</option>
                <option value={TicketCategory.INVESTIGATION}>Investigation</option>
                <option value={TicketCategory.GENERAL}>General</option>
                <option value={TicketCategory.OTHER}>Other</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-base font-semibold text-gray-900 mb-2 text-start">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Please describe your issue or question in detail..."
                rows={8}
                maxLength={2000}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {message.length}/2000 characters
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Support ticket submitted successfully! We'll get back to you soon.
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                disabled={isLoading || success}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-xl text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
